<template>
	<AccessibleSection
		name="announcements"
		class="announcements-wrapper"
		:tabOrder="tabOrder"
		:wait="250"
		:class="{ 'has-detail': !!detailAnnouncementId }"
		v-if="store.auth.user.organizations[0].announcements_enabled"
	>
		<div class="announcements-header">
			<CloseIcon
				v-if="!!detailAnnouncementId"
				class="detail-close"
				@click="closeDetail"
			/>
			<div
				class="announcements-header-org"
				v-text="store.auth.user.organizations[0].name"
			/>
			<div
				class="announcements-header-title"
				v-text="$t('announcements_name_multiple')"
			/>

			<div class="announcement-create-button">
				<Button
					v-if="canCreateAnnouncements && !showComposer"
					primary
					pill
					@click="openComposer"
				>
					{{ $t('announcements_new_title') }}
				</Button>
			</div>
		</div>

		<div class="announcements-container">
			<div class="announcements-loading" v-if="isLoading">
				<Spinner />
			</div>

			<Tabs
				class="announcement-tabs"
				v-if="canCreateAnnouncements"
				stretch
				:tabs="tabs"
				:defaultSelected="activeType"
				selectedcolor="#2c2d31"
				@change="onChangeTab"
			>
				<template v-slot="{ tab }">
					<div class="announcement-tab" v-text="tab.text" />
				</template>
			</Tabs>

			<ScrollToLoad
				class="announcements-list"
				:loading="isLoadingMore"
				:class="{ empty: !announcements.length }"
				@bottom="loadNextPage"
				:threshold="0.5"
			>
				<AnnouncementPreview
					v-for="announcement in announcements"
					:key="announcement.id"
					:announcementId="announcement.id"
					:active="announcement.id === detailAnnouncementId"
					@click="viewAnnouncement(announcement)"
				/>

				<div
					class="empty-state"
					v-if="!isLoading && !announcements.length"
					v-t="'announcements_list_scheduled_empty'"
				/>
			</ScrollToLoad>
		</div>

		<AnnouncementComposer
			v-if="showComposer"
			:type="composerType"
			@close="showComposer = false"
			@success="showDetail"
		/>

		<AnnouncementDetail :announcementId="detailAnnouncementId" />
	</AccessibleSection>
</template>

<script>
import store, {
	canSendAnnouncements,
	getAnnouncementWithSender,
	getSentAnnouncementsWithSenders,
	getScheduledAnnouncementsWithSenders,
	canSendGroupAnnouncements,
	canSendOrgAnnouncements,
} from '@/store'
import AnnouncementComposer, {
	types as composerTypes,
} from './AnnouncementComposer'
import { AccessibleSection } from '@/components/common/Accessible'
import AnnouncementDetail from './AnnouncementDetail'
import AnnouncementPreview from './AnnouncementPreview'
import { Button, Spinner, ScrollToLoad, Tabs } from '@/components/common'
import { CloseIcon } from '@/icons'
import {
	fetchAnnouncement,
	fetchAnnouncements,
	markAnnouncementAsRead,
} from '@/api/announcement'

const types = {
	RECEIVED: 'RECEIVED',
	SCHEDULED: 'SCHEDULED',
}

export default {
	name: 'Announcements',
	components: {
		AccessibleSection,
		AnnouncementDetail,
		AnnouncementPreview,
		Button,
		Spinner,
		ScrollToLoad,
		Tabs,
		AnnouncementComposer,
		CloseIcon,
	},

	data() {
		const announcementsByType = this.updateAnnouncementsFromStore()
		return {
			store,
			types,
			announcementsByType,
			activeType: types.RECEIVED,
			condensedSize: false,
			detailAnnouncementId: null,
			showComposer: false,
			composerType: null,
			isLoading: true,
			isLoadingMore: false,
		}
	},

	async mounted() {
		this.$onEventBus({
			condensedSize: (data) => {
				this.condensedSize = data
			},
			createNewAnnouncement: () => {
				this.openComposer()
			},
			showDetail: (data) => {
				this.$nextTick(() => {
					this.showDetail(data)
				})
			},
		})

		this.$onEventBus(
			'announcement-update',
			() => this.updateAnnouncementsFromStore(),
			25
		)

		this.fetchedResponses = {}
		await this.fetchAnnouncementsOfType(types.RECEIVED)

		const { id: detailId } = this.$route.params
		const { create, ...query } = this.$route.query
		if (create) {
			this.openComposer()
			this.$router.replace({ query })
		}
		if (detailId) {
			await this.showDetail(detailId)
			if (!this.detailAnnouncementId) {
				this.$router.replace({ params: { id: null } })
			}
		} else {
			EventBus.$emit('hamburgerDisabled', false)
		}
	},

	beforeRouteUpdate(to, from, next) {
		this.showDetail(to.params.id)
		next()
	},

	computed: {
		announcements() {
			return this.announcementsByType[this.activeType] || []
		},

		canCreateAnnouncements() {
			return canSendAnnouncements()
		},

		tabs() {
			return [
				{ id: types.RECEIVED, text: this.$t('announcements_filter_received') },
				{
					id: types.SCHEDULED,
					text: this.$t('announcements_filter_scheduled'),
				},
			]
		},

		tabOrder() {
			return [
				{ css: '.announcement-create-button .pronto-btn', clickOnEnter: true },
				{
					css: '.announcements-container',
					Enter: () => {
						return [
							{ css: '.tab-item:not(.selected)', clickOnEnter: true },
							{
								all: '.announcement-preview',
								preventNextOnTab: true,
								arrows: true,
								clickOnEnter: true,
								Enter: async (e, item, vm, accessible) => {
									await accessible.sleep(500)
									accessible.setTabList(accessible.tabOrder)
									accessible.moveToNextElem()
								},
							},
						]
					},
				},
				{
					css: '.announcement-detail',
					Enter: () => {
						return [
							{ css: '.edit-link', clickOnEnter: true },
							{ css: '.seenby-btn', clickOnEnter: true },
							{ css: '.dots-menu .actions' },
							{ css: '.announcement-targets.clickable', clickOnEnter: true },
							{
								css: '.announcement-media .media-file-container',
								clickOnEnter: true,
							},
							{
								css: '.announcement-media .pronto-visual-image',
								clickOnEnter: true,
							},
						]
					},
				},
			]
		},
	},

	watch: {
		confirmAnnouncementDelete: function (val) {
			if (!val) {
				this.currentDeleteId = null
			}
		},
		activeType(type) {
			// if we haven't fetched this type before, then fetch the first page
			if (!this.fetchedResponses[type]) {
				this.isLoading = true
				this.fetchAnnouncementsOfType(type)
			}
		},
	},

	methods: {
		updateAnnouncementsFromStore() {
			const announcementsByType = {
				[types.RECEIVED]: getSentAnnouncementsWithSenders(),
				[types.SCHEDULED]: getScheduledAnnouncementsWithSenders(),
			}
			this.announcementsByType = announcementsByType
			return announcementsByType
		},

		async fetchAnnouncementsOfType(query, payload = {}) {
			if (!types[query]) return

			try {
				const resp = await fetchAnnouncements({
					query,
					perPage: 20,
					...payload,
				})
				this.fetchedResponses[query] = resp
			} catch (e) {
				console.error(e)
				this.$alert.error(e)
			}
			this.isLoading = false
			this.isLoadingMore = false
		},

		async loadNextPage(isAtBottom) {
			if (!isAtBottom || this.isLoading || this.isLoadingMore) return false
			const { cursors } = this.fetchedResponses[this.activeType] || {}
			if (!cursors?.next) return false

			this.isLoadingMore = true
			await this.fetchAnnouncementsOfType(this.activeType, {
				cursor: cursors.next,
			})
		},

		closeDetail() {
			this.showDetail(null)
			this.$router.replace('/announcements')
		},

		async showDetail(announcementId) {
			if (!announcementId) {
				this.detailAnnouncementId = null
				EventBus.$emit('hamburgerDisabled', false)
				return
			}

			// check to see if we already have announcement
			announcementId = parseInt(announcementId, 10)
			let announcement = getAnnouncementWithSender(announcementId)

			if (!announcement) {
				// we don't have announcement yet, so fetch it
				try {
					this.isLoading = true
					await fetchAnnouncement(announcementId)
					announcement = getAnnouncementWithSender(announcementId)
				} catch (e) {}
			}
			if (!announcement) {
				this.isLoading = false
				return this.closeDetail()
			}

			if (announcement.isUnread && announcement.isSent) {
				markAnnouncementAsRead(announcement.id)
			} else if (announcement.isScheduled) {
				this.activeType = types.SCHEDULED
			}

			this.detailAnnouncementId = announcement.id
			this.isLoading = false
			EventBus.$emit('hamburgerDisabled', true)
		},

		viewAnnouncement(announcement) {
			if (this.$route.path.includes(`/${announcement.id}`)) return
			this.$router.replace(`/announcements/${announcement.id}`)
		},

		onChangeTab({ id }) {
			this.activeType = id
		},

		openComposer() {
			if (!this.canCreateAnnouncements) {
				// if can't send any announcements, just bail
				this.showComposer = false
				this.composerType = null
				return false
			}
			const groupOk = canSendGroupAnnouncements()
			const orgOk = canSendOrgAnnouncements()
			if (!groupOk || !orgOk) {
				// if can't send both types, figure out which type we can send
				this.composerType = orgOk ? composerTypes.ORG : composerTypes.GROUP
				this.showComposer = true
				return
			}
			// user will have to pick which type of announcement to send
			this.composerType = null
			this.showComposer = true
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

@media screen and (max-width: 1000px) {
	.announcements-wrapper {
		grid-template-columns: 100% !important;
		grid-template-rows: 60px auto !important;
		grid-template-areas:
			'header'
			'main' !important;
		height: 100vh !important;

		&.has-detail {
			.announcements-container {
				display: none;
			}
			.announcement-detail {
				display: flex;
				overflow-y: auto;
				:deep(.announcement-view) {
					height: unset;
					max-height: unset;
				}
			}
			.detail-close {
				display: block;
			}
		}

		.announcements-container {
			grid-area: main;
		}
		.announcement-detail {
			grid-area: main;
			display: none;
		}
		.detail-close {
			display: none;
		}

		.announcement-create-button {
			display: none;
		}
	}
}

.hide-scroll {
	overflow-y: hidden;
}

.announcements-wrapper {
	display: grid;
	grid-template-columns: 418px auto;
	grid-template-rows: 60px auto;
	grid-template-areas:
		'header header'
		'sidebar details';
	height: calc(100vh - #{$search-bar-height});
	width: 100%;
	border-left: 1px solid var(--neutral-40040);
}

.announcements-header {
	grid-area: header;
	@include flex-column-center;
	justify-content: center;
	position: relative;
	border-bottom: 1px solid var(--neutral-40040);

	.announcements-header-title {
		font-family: Avenir-Heavy;
		font-size: 20px;
	}

	.announcements-header-org {
		font-family: Avenir-Roman;
		font-size: 8px;
		letter-spacing: 1px;
		text-transform: uppercase;
	}

	.announcement-create-button {
		position: absolute;
		top: 9px;
		right: 16px;
	}

	.detail-close {
		display: none;
		position: absolute;
		top: 16px;
		left: 16px;
		font-size: 32px;
	}
}

.new-announcement-plus {
	display: none;
	height: 18px;
	vertical-align: sub;
}

.announcement-text {
	display: unset;
}

.read-header {
	padding-top: 15px;
}

.announcement-tabs {
	padding-top: 6px;
	background: var(--neutral-0);
	.announcement-tab {
		@include flex(12px);
		.tab-count-badge {
			@include flex-center;
			height: 20px;
			width: 20px;
			border-radius: 100px;
			font-size: 12px;
			background: var(--blue-300);
			color: white;
		}
	}
	:deep(.tabs) {
		padding: 0 !important;
	}
}

.announcements-container {
	position: relative;
	grid-area: sidebar;
	background-color: #fafafa;
	@include flex-column;
	max-height: 100%;
	overflow: hidden;

	.announcements-list {
		padding: 16px 24px;
		flex-grow: 1;
		overflow-y: auto;

		&.empty {
			@include flex-column-center;
			.empty-state {
				margin-top: 80px;
			}
		}
	}
	.announcements-loading {
		@include flex-center;
		@include absolute-cover(1);
		background: var(--neutral-100);
		opacity: 0.7;
		:deep(.pronto-spinner) {
			font-size: 50px;
		}
	}
}

.announcements-empty {
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	margin: auto;
	width: 250px;
	height: 250px;
}

.announcements-empty-icon img {
	width: 50px;
	padding-top: 40px;
	opacity: 0.5;
}

.announcements-empty-icon-background {
	width: 120px;
	height: 120px;
	background-color: #fafafa;
	margin: auto;
	line-height: 50px;
	text-align: center;
	border-radius: 100%;
}

.no-announcements-text {
	width: 154px;
	height: 46px;
	font-family: Avenir-Roman;
	font-size: 17px;
	text-align: center;
	color: #2c2d30;
	margin: 20px auto;
}

.no-pointer-events {
	pointer-events: none;
}

.selected-announcement {
	background-color: #f0f2f3;
	outline: 0;
}

.selected-announcement:hover {
	box-shadow: unset;
}

.loading-more-announcements {
	position: absolute;
	bottom: 10px;
	left: 150px;
	font-family: Avenir-Roman;
	font-size: 14px;
}

.announcements-banner-closing {
	animation-name: closebanner;
	animation-delay: 1200ms;
	animation-duration: 400ms;
}

.announcement-list-tools {
	height: 50px;
	/*opacity: 0;*/
	display: flex;
	justify-content: space-between;
	padding: 10px 5px;
	margin-left: 20px;
	/*transition: all 0.5s ease-in-out;*/
}

.announcement-list-tools li {
	list-style-type: none;
	font-family: Avenir-Heavy;
	font-size: 18px;
	color: #4f5053;
	cursor: pointer;
	display: inline-block;
}

.announcement-list-tools li.selected {
	color: #01dab6;
	border-bottom: 2px solid #01dab6;
	padding-bottom: 1px;
}

.scheduled-link-badge {
	border-radius: 20px;
	background-color: #01dab6;
	font-family: Avenir-Heavy;
	font-size: 12px;
	color: #ffffff;
	padding: 1px 5px 0;
	display: inline-block;
	vertical-align: middle;
	margin-top: -5px;
}

.unread-announcement {
	content: '';
	width: 4px;
	position: absolute;
	left: 0;
	top: 0;
	bottom: 0;
	border-top-left-radius: 100px;
	border-bottom-left-radius: 100px;
	background-color: var(--blue-300);
}

.nothing-selected-wrapper {
	width: calc(100vw - 735px);
	height: 100%;
	background: #fff;
	position: absolute;
	top: 80px;
	right: 0;
}

.nothing-selected {
	position: absolute;
	top: 50%;
	left: 50%;
	margin-left: -100px;
	margin-top: -140px;
}

.nothing-selected-text {
	width: 170px;
	height: 46px;
	font-family: Avenir-Medium;
	font-size: 15px;
	text-align: center;
	color: #4f5053;
	margin: 20px auto;
}

.announcement-discard-title {
	font-family: Avenir-Medium;
	font-size: 18px;
	text-align: center;
	color: #4f5053;
	margin-bottom: 16px;
	padding: 5px 70px;
}

.announcement-discard-description {
	font-family: Avenir-Book;
	font-size: 16px;
	text-align: center;
	color: #4f5053;
}

@keyframes closebanner {
	from {
		top: 130px;
	}
	to {
		top: 0;
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';
.app-layout {
	&.search:not(.condensed) {
		.announcements-wrapper {
			height: calc(100vh - #{$search-bar-height});
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.announcements-wrapper {
		.announcements-header {
			background: var(--neutral-0);
		}
		.announcements-list {
			background: var(--neutral-100);
		}
	}
}
</style>
